import { useCallback, useEffect, useRef, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { MdPhoneAndroid } from "react-icons/md";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";

type ChangePhoneNumberModalProps = {
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  phone: string;
};


function ChangePhoneNumberModal({
  setNeedRefetch,
  phone,
}: ChangePhoneNumberModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [otpToken, setOtpToken] = useState<string | null>(null);
  const { t } = useTranslation();
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const userToken = useSelector(
    (state: { auth: { token: string } }) => state.auth.token
  );

  // Send OTP
  async function sendOtp(phone: string) {
    const url = `${BASE_URL}/api/dashboard/users/otp`;
    const toastId = toast.loading(
      t("changePhoneNumberModal.otpSent")  as string
    );

    try {
      const response = await axios.post(
        url,
        { phone },
        {
          headers: {
            Authorization: `${userToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setOtpToken(response.data.token);
        toast.success(
          t("changePhoneNumberModal.otpSent") as string
        );
        setStep(2);

      } else {
        toast.error(
          response.data.error ||      t("changePhoneNumberModal.otpFailed") as string
        )
      }
    } catch (error) {
      toast.error(
        axios.isAxiosError(error)
          ? error.response?.data?.error ||
               t("changePhoneNumberModal.otpFailed")
          :t("changePhoneNumberModal.unexpectedError") 
      );
    } finally {
      toast.dismiss(toastId);
    }
  }

  // Change Phone Number
  async function changePhoneNumber(otp: string) {
    const url = `${BASE_URL}/api/dashboard/users/contact-info`;
    const toastId = toast.loading(
      t("changePhoneNumberModal.changingPhone")as string
    );

    try {
      const response = await axios.put(
        url,
        { otp, token: otpToken, phone },
        {
          headers: {
            Authorization: `${userToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        toast.success(
          t("changePhoneNumberModal.phoneChanged") as string
        );
        setNeedRefetch(true);
      } else {
        toast.error(
          response.data.error ||
          t("changePhoneNumberModal.phoneChangeFailed")  as string
        );
      }
    } catch (error) {
      toast.error(
        axios.isAxiosError(error)
          ? error.response?.data?.error ||
          t("changePhoneNumberModal.phoneChangeFailed") 
          : t("changePhoneNumberModal.unexpectedError") 
      );
    } finally {
      toast.dismiss(toastId);
    }
  }

  // Formik setup
  const formik = useFormik({
    initialValues: {
      phoneNumber: phone,
      otp: "",
    },
    validationSchema: Yup.object({
      phoneNumber: step === 1
        ? Yup.string()
          .required( t("changePhoneNumberModal.phoneRequired")  as string)
          .matches(
            /^5\d{8}$/,
            t("changePhoneNumberModal.invalidPhone")  as string
          )
        : Yup.string(), // Default case for step !== 1
      otp: step === 2
        ? Yup.string()
          .required(t("changePhoneNumberModal.codeRequired") as string)
        : Yup.string(), // Default case for step !== 2
    }),

    onSubmit: async (values) => {
      if (step === 1) {
        // Step 1: Send OTP
        await sendOtp(values.phoneNumber);
      } else if (step === 2) {
        await changePhoneNumber(values.otp);
      }
    },
  });

  // Handle click outside to close modal
  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsModalOpen(false);
      }
    },
    [modalRef]
  );

  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isModalOpen, handleOutsideClick]);

  return (
    <div>
      <button
        onClick={() => setIsModalOpen(true)}
        className="absolute end-0 top-1/2 flex h-full w-[75px] -translate-y-1/2 transform cursor-pointer items-center justify-center self-end rounded-e-md border border-sidebar bg-white text-sm font-medium capitalize text-sidebar md:text-base"
      >
        {t("changePhoneNumberModal.update")}
      </button>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-[70px]"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            ref={modalRef}
            className="flex w-[95%] max-w-3xl flex-col gap-7 rounded-lg bg-white px-2 py-3 shadow-lg sm:px-4 sm:py-4"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {t("changePhoneNumberModal.updatePhoneNumber")}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-600 hover:text-gray-800"
              >
                <IoMdClose size={24} className="text-red-600" />
              </button>
            </div>

            <form onSubmit={formik.handleSubmit} className="space-y-4">
              {step === 1 && (
                <div className="flex flex-col">
                  <label className="text-sm text-gray-600">
                    {t("changePhoneNumberModal.newPhoneNumber")}
                  </label>
                  <div className="relative my-2 w-full">
                    <input
                      type="text"
                      name="phoneNumber"
                      value={formik.values.phoneNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="w-full rounded-md border border-gray-300 p-2 pe-[5px] ps-8 focus:outline-none focus:ring-2 focus:ring-border"
                    />
                    <MdPhoneAndroid
                      size={20}
                      className="absolute start-2 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                  </div>
                  {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                    <div className="text-sm text-red-600">
                      {formik.errors.phoneNumber}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="mt-3 rounded bg-sidebar py-2 px-4 text-skyblue w-fit self-end"
                  >
                    {t("changePhoneNumberModal.sendCode")}
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="flex flex-col">
                  <label className="text-sm text-gray-600 mb-1">
                    {t("changePhoneNumberModal.verificationCode")}
                  </label>
                  <input
                    type="text"
                    name="otp"
                    maxLength={4}
                    placeholder={t("changePhoneNumberModal.enterCode") as string}
                    value={formik.values.otp}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="w-full rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-border"
                  />
                  {formik.touched.otp && formik.errors.otp && (
                    <div className="text-sm text-red-600">
                      {formik.errors.otp}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="mt-3 rounded bg-sidebar py-2 px-4 text-skyblue w-fit self-end"
                  >
                    {t("changePhoneNumberModal.verifyAndChange")}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChangePhoneNumberModal;
